import React, { useEffect, useState } from 'react';
import { Button, Collapse, Tabs } from 'antd';
import '../../shipment/ShipmentTrack.css';
import './ReceptionView.less';
import { useHistory } from 'react-router-dom';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import moment from 'moment';

import { usePackagingFlow } from '../../../context/PackagingFlowContext';
import { PackagingFlowItem } from '../../../queries/api/types';
import { usePackagingConfig } from '../../../queries/package';
import { fetchLoadingSessionsPackage } from '../../../queries/api/loading-sessions';
import { mapPackageFlowItems } from '../../../utils/utils';
import { useNotification } from '../../notification/Notification';

const { TabPane } = Tabs;
const { Panel } = Collapse;
const ReceptionView: React.FC = () => {
    const history = useHistory();
    const { loadingSession, selectedStore, setPackageFlow, packageFlow, setPackageFlowId } = usePackagingFlow();
    const [pkgItemMap, setPkgItemMap] = useState<Record<string, PackagingFlowItem>>({});
    const { data: packageConfig } = usePackagingConfig();
    const { showNotification } = useNotification();
    const [, setPkgFlowItems] = useState<PackagingFlowItem[]>([]);
    useEffect(() => {
        if (!packageFlow && packageConfig) {
            const fetchAndSetLoadingSessionsPackage = async (loadingSessionId: string | undefined) => {
                try {
                    const response = await fetchLoadingSessionsPackage(loadingSessionId);
                    if (response) {
                        setPackageFlow(response);
                        setPackageFlowId(response?.id ?? null);
                        const flowMap = mapPackageFlowItems(response.packages);
                        setPkgItemMap(flowMap);
                    }
                } catch (error) {
                    console.error('Error fetching loading sessions packages:', error);
                }
            };
            if (loadingSession?.id) {
                fetchAndSetLoadingSessionsPackage(loadingSession.id);
            }
        }
    }, [loadingSession, packageConfig, packageFlow, setPackageFlow, setPackageFlowId]);

    useEffect(() => {
        if (packageConfig && packageFlow) {
            const flowMap = mapPackageFlowItems(packageFlow.packages);
            setPkgItemMap(flowMap);
        }
    }, [packageConfig, packageFlow]);

    // const handleSessionSelect = (item: BaseModel, category: BaseModel) => {
    //     setPackageType(item);
    //     setPackageCategory(category);
    //     history.push(`/receptionDetails`);
    // };

    const handleIncrement = (packageFlowItem: PackagingFlowItem, countType: 'goodCount' | 'badCount') => {
        const updatedMap = { ...pkgItemMap };
        updatedMap[packageFlowItem.package as string][countType] = packageFlowItem[countType] + 1;
        setPkgItemMap(updatedMap);
        setPkgFlowItems((prev) =>
            prev.map((item) =>
                item.package === packageFlowItem.package ? { ...item, [countType]: item[countType] + 1 } : item
            )
        );
    };

    const handleDecrement = (packageFlowItem: PackagingFlowItem, countType: 'goodCount' | 'badCount') => {
        const updatedMap = { ...pkgItemMap };
        updatedMap[packageFlowItem.package as string][countType] = Math.max(0, packageFlowItem[countType] - 1);
        setPkgItemMap(updatedMap);
        setPkgFlowItems((prev) =>
            prev.map((item) =>
                item.package === packageFlowItem.package
                    ? { ...item, [countType]: Math.max(0, item[countType] - 1) }
                    : item
            )
        );
    };

    const handleInputChange = (
        packageFlowItem: PackagingFlowItem,
        countType: 'goodCount' | 'badCount',
        value: number
    ) => {
        const updatedMap = { ...pkgItemMap };
        updatedMap[packageFlowItem.package as string][countType] = value;
        setPkgItemMap(updatedMap);
        setPkgFlowItems((prev) =>
            prev.map((item) => (item.package === packageFlowItem.package ? { ...item, [countType]: value } : item))
        );
    };
    const handleSave = () => {
        if (packageFlow) {
            const hasValidCount = packageFlow.packages.some((pkg) => pkg.goodCount > 0 || pkg.badCount > 0);

            if (!hasValidCount) {
                showNotification(
                    'error',
                    "Échec de l'analyse",
                    `Au moins un package doit avoir un nombre de bons ou de mauvais supérieur à 0.`
                );
                return;
            }
            history.push('/receptionComment');
        }
    };

    const renderPackagesByCategory = (categoryId: string) => {
        const typesInCategory = Array.from(
            new Set(packageConfig?.packages.filter((pkg) => pkg.category.id === categoryId).map((pkg) => pkg.type))
        );

        return typesInCategory.map((type) => (
            <Collapse key={type.id} style={{ marginBottom: '10px' }}>
                <Panel header={type.name} key={type.id}>
                    {packageConfig?.packages
                        .filter((pkg) => pkg.category.id === categoryId && pkg.type.id === type.id)
                        .map((pkg) => (
                            <div key={pkg.model.id} className="package-item">
                                <div style={{ marginBottom: '10px' }}>
                                    <strong className="model-view">{pkg.model.name}</strong>
                                </div>
                                <div className="flex-direction flex">
                                    <div className="quantity-controls quantity">
                                        <span>Bon état</span>
                                        <MinusOutlined
                                            onClick={() => handleDecrement(pkgItemMap[pkg.id], 'goodCount')}
                                            className="minus"
                                        />
                                        <input
                                            type="number"
                                            value={pkgItemMap[pkg.id]?.goodCount || 0}
                                            min="0"
                                            className="input-box"
                                            onChange={(e) =>
                                                handleInputChange(
                                                    pkgItemMap[pkg.id],
                                                    'goodCount',
                                                    parseInt(e.target.value)
                                                )
                                            }
                                        />
                                        <PlusOutlined
                                            onClick={() => handleIncrement(pkgItemMap[pkg.id], 'goodCount')}
                                            className="plus"
                                        />
                                    </div>
                                    &nbsp; &nbsp;
                                    <div className="quantity-controls quantity">
                                        <span>Cassée</span>
                                        <MinusOutlined
                                            onClick={() => handleDecrement(pkgItemMap[pkg.id], 'badCount')}
                                            className="minus"
                                        />
                                        <input
                                            type="number"
                                            value={pkgItemMap[pkg.id]?.badCount || 0}
                                            min="0"
                                            className="input-box"
                                            onChange={(e) =>
                                                handleInputChange(
                                                    pkgItemMap[pkg.id],
                                                    'badCount',
                                                    parseInt(e.target.value)
                                                )
                                            }
                                        />
                                        <PlusOutlined
                                            onClick={() => handleIncrement(pkgItemMap[pkg.id], 'badCount')}
                                            className="plus"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                </Panel>
            </Collapse>
        ));
    };

    return (
        <div className="reception-view bg-dash">
            <header className="header">
                <div className="location">
                    <p>{selectedStore?.name}</p>
                    <p>Remorque: {loadingSession?.trailerNumber}</p>
                </div>
                <div className="info">
                    <p>
                        {moment(loadingSession?.createdAt).format('DD/MM/YYYY')} -{' '}
                        {moment(loadingSession?.createdAt).format(' HH:mm')}
                    </p>
                    <p>Transporteur: {loadingSession?.transporter.name}</p>
                </div>
            </header>

            <Tabs defaultActiveKey={packageConfig?.packageCategories?.[0]?.id || '1'}>
                {packageConfig?.packageCategories
                    .filter((value, index, self) => self.findIndex((t) => t.id === value.id) === index) // Remove duplicates based on `id`
                    .map((category) => (
                        <TabPane tab={category.name} key={category.id}>
                            {renderPackagesByCategory(category.id)}
                        </TabPane>
                    ))}
            </Tabs>
            <div className="bottom">
                <footer>
                    <Button type="primary" onClick={handleSave} style={{ width: '100%', marginTop: '20px' }}>
                        Valider
                    </Button>
                </footer>
            </div>
        </div>
    );
};

export default ReceptionView;
